import type { SmartPulseAgentTool, SmartPulseToolDefinition } from "@tylertech/smart-pulse-core";
import type { SmartPulseToolResult } from "@tylertech/smart-pulse-shared";
import { CxBridgeService, EscalateArgs, SupportAreas } from "./cxBridge.service";

const AreaParameter = {
    type: "string",
    enum: [...SupportAreas],
    description: "Which support team should take this chat. Decide from the page the user is on "
        + "(pageUrl in your context) and what the conversation is about; if they disagree, the conversation wins. "
        + "\"utilities\": utility billing, customer accounts, meters, service orders, cashiering or cash collections, sales tax. "
        + "\"financials\": general ledger, accounts payable, purchasing, budgets, fixed assets, inventory, "
        + "project accounting, bank reconciliation, payroll, human resources. "
        + "\"property_tax\": property tax billing, collections, and assessments. "
        + "If neither the page nor the conversation makes the area clear, ask the user one short question "
        + "(for example: is this about utility billing, finance and payroll, or property tax?) before calling."
};

export class CxEscalationTool implements SmartPulseAgentTool<EscalateArgs>
{
    constructor(private readonly $cxBridgeService: CxBridgeService) {}

    getDefinition(): SmartPulseToolDefinition
    {
        const routesByArea = this.$cxBridgeService.routesByArea;
        return {
            name: "escalate",
            displayName: "Escalate to live support",
            description: "Hand the conversation to a live Tyler support agent in this chat window. "
                + "Call this when the user explicitly asks for a person, when you cannot resolve their issue "
                + "after a genuine attempt, or when the issue needs account access or actions you do not have. "
                + "Do not call it for questions you can still answer. Before calling, make sure you know what "
                + "the user was trying to do and what went wrong, because the agent will only see your summary. "
                + "Call it once per conversation.",
            parameters: {
                type: "object",
                properties: {
                    reason: {
                        type: "string",
                        description: "One sentence stating why a live agent is needed, written for the agent. "
                            + "Example: \"User cannot post a payroll batch and receives an error I could not resolve.\""
                    },
                    priority: {
                        type: "string",
                        enum: ["low", "medium", "high", "urgent"],
                        description: "Triage level for the support queue. "
                            + "\"urgent\": the user is blocked on a time-sensitive task due today, such as payroll, "
                            + "a filing or posting deadline, or cashiering at a live counter. "
                            + "\"high\": a core workflow is broken or data looks wrong and there is no workaround. "
                            + "\"medium\": a question or problem you could not resolve but the user can keep working. "
                            + "\"low\": a feature request, feedback, or a how-to question with no immediate need. "
                            + "Default to \"medium\" when unsure."
                    },
                    conversationSummary: {
                        type: "string",
                        description: "The first message the support agent reads, so write it for them. Include: "
                            + "what the user was trying to do and where in the application, what they tried and "
                            + "what happened, any error text, record numbers, dates, or amounts they mentioned, "
                            + "and what you already told them so the agent does not repeat it. "
                            + "Use plain factual sentences. Do not include greetings or your own commentary."
                    },
                    ...(routesByArea && { area: AreaParameter })
                },
                required: routesByArea ? ["reason", "conversationSummary", "area"] : ["reason", "conversationSummary"]
            }
        };
    }

    async execute(args: EscalateArgs): Promise<SmartPulseToolResult>
    {
        const result = await this.$cxBridgeService.executeEscalate(args);
        return {
            status: result.success ? "ok" : "error",
            message: result.message,
            data: {
                escalationSummary: result.escalationSummary,
                error: result.error,
                timestamp: result.timestamp
            }
        };
    }
}
